import React, {useContext, useState} from 'react';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getDefaultValueOfComponent, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    ITEMS_VIEW_COMMAND_ACTION,
    ITEMS_VIEW_COMMAND_ACTION_COPY_URL,
    ITEMS_VIEW_COMMAND_ACTION_DELETE,
    ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD,
    ITEMS_VIEW_COMMAND_ACTION_EDIT,
    ITEMS_VIEW_COMMAND_ACTION_FAVORITE,
    ITEMS_VIEW_COMMAND_ACTION_MORE,
    ITEMS_VIEW_COMMAND_ACTION_PREVIEW,
    ITEMS_VIEW_COMMAND_ACTION_VIEW,
    ITEMS_VIEW_GRID_COMMAND_ACTION_BUTTONS_IN_MORE_LIST,
    ITEMS_VIEW_GRID_COMMAND_ACTION_BUTTONS_IN_ROW_LIST,
    ITEMS_VIEW_GRID_COMMAND_MAX_ACTION_BUTTONS_IN_ROW,
    ITEMS_VIEW_GRID_COMMAND_MOBILE_ACTION_BUTTONS_IN_MORE_LIST,
    ITEMS_VIEW_GRID_COMMAND_MOBILE_ACTION_BUTTONS_IN_ROW_LIST,
    ITEMS_VIEW_GRID_COMMAND_MOBILE_MAX_ACTION_BUTTONS_IN_ROW,
    ITEMS_VIEW_LIST_COMMAND_ACTION_BUTTONS_IN_MORE_LIST,
    ITEMS_VIEW_LIST_COMMAND_ACTION_BUTTONS_IN_ROW_LIST,
    ITEMS_VIEW_LIST_COMMAND_MAX_ACTION_BUTTONS_IN_ROW,
    ITEMS_VIEW_LIST_COMMAND_MOBILE_ACTION_BUTTONS_IN_MORE_LIST,
    ITEMS_VIEW_LIST_COMMAND_MOBILE_ACTION_BUTTONS_IN_ROW_LIST,
    ITEMS_VIEW_LIST_COMMAND_MOBILE_MAX_ACTION_BUTTONS_IN_ROW,
    ITEMS_VIEW_TABLE_COMMAND_ACTION_BUTTONS_IN_MORE_LIST,
    ITEMS_VIEW_TABLE_COMMAND_ACTION_BUTTONS_IN_ROW_LIST,
    ITEMS_VIEW_TABLE_COMMAND_MAX_ACTION_BUTTONS_IN_ROW,
    ITEMS_VIEW_TABLE_COMMAND_MOBILE_ACTION_BUTTONS_IN_MORE_LIST,
    ITEMS_VIEW_TABLE_COMMAND_MOBILE_ACTION_BUTTONS_IN_ROW_LIST,
    ITEMS_VIEW_TABLE_COMMAND_MOBILE_MAX_ACTION_BUTTONS_IN_ROW
} from '../../../init/ui_component_constants';
import {CommandActionWrapper} from './CommandActionsBar.style';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {Button, MenuItem, Popover} from '@bosch/react-frok';
import {t} from 'i18next';
import {convertToStringArray, executeAction, getFileExtension, isPreviewApplicable} from '../../../utils';
import {AuthContext} from '../../../context/AuthContext';
import {hasWriteOwnerAccess} from '../../../utils/UserUtils';
import {StorageObject} from '../../../models/StorageObject';
import {GridCustomButton} from '../ItemGrid/ItemGridTile.style';
import {LayoutViewType} from '../../../models/LayoutViewType';
import {isFileDownloadable} from '../../../utils/DtoUtils';
import {isFile} from '../../../utils/StorageObjectUtils';
import {
    defaultItemsViewItemsInMoreGrid,
    defaultItemsViewItemsInMoreList,
    defaultItemsViewItemsInMoreTable,
    defaultItemsViewItemsInRowGrid,
    defaultItemsViewItemsInRowList,
    defaultItemsViewItemsInRowTable,
    defaultItemsViewItemsMobileInRowList,
    defaultItemsViewItemsMobileInRowTable,
    defaultItemsViewMaxItemsInRowGrid,
    defaultItemsViewMaxItemsInRowList,
    defaultItemsViewMaxItemsInRowTable,
    defaultItemsViewMobileItemsInMoreGrid,
    defaultItemsViewMobileItemsInMoreList,
    defaultItemsViewMobileItemsInMoreTable,
    defaultItemsViewMobileItemsInRowGrid,
    defaultItemsViewMobileMaxItemsInRowGrid,
    defaultItemsViewMobileMaxItemsInRowList,
    defaultItemsViewMobileMaxItemsInRowTable
} from '../../../init/ui_component_defaults';

export interface CommandActionsBarProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly isMobileView: boolean;
    readonly layoutView: LayoutViewType;
    readonly item: StorageObject;
    readonly editMode: boolean;
    readonly publicOnlyAccess: boolean;
    readonly isFavorite: boolean;
    onDelete: () => void;
    onDownload: () => void;
    onEdit: () => void;
    onDetails: () => void;
    onFavorite: () => void;
    onPreview: () => void;
    onCopyUrl: () => void;
}

const reverseDirection = (layoutView: LayoutViewType): boolean => {
    return [LayoutViewType.LIST, LayoutViewType.TABLE].includes(layoutView) as boolean;
};

const isDownloadFeatureActive = (layoutView: LayoutViewType): boolean => {
    return [LayoutViewType.TABLE].includes(layoutView) as boolean;
};

const getDefaultMaxActionButtonsInRow = (templatePropertySettings: Map<string, TemplatePropertySettings>, isMobileView: boolean, layoutView: LayoutViewType): number => {
    let maxActionButtons = 0;
    switch (layoutView) {
        case LayoutViewType.GRID:
            if (isMobileView) {
                maxActionButtons = Number(getDefaultValueOfComponent(ITEMS_VIEW_GRID_COMMAND_MOBILE_MAX_ACTION_BUTTONS_IN_ROW, templatePropertySettings));
                if ((maxActionButtons === undefined) || (!maxActionButtons) || isNaN(maxActionButtons) || (maxActionButtons <= 0)) {
                    maxActionButtons = defaultItemsViewMobileMaxItemsInRowGrid;
                }
            } else {
                maxActionButtons = Number(getDefaultValueOfComponent(ITEMS_VIEW_GRID_COMMAND_MAX_ACTION_BUTTONS_IN_ROW, templatePropertySettings));
                if ((maxActionButtons === undefined) || (!maxActionButtons) || isNaN(maxActionButtons) || (maxActionButtons <= 0)) {
                    maxActionButtons = defaultItemsViewMaxItemsInRowGrid;
                }
            }
            break;
        case LayoutViewType.LIST:
            if (isMobileView) {
                maxActionButtons = Number(getDefaultValueOfComponent(ITEMS_VIEW_LIST_COMMAND_MOBILE_MAX_ACTION_BUTTONS_IN_ROW, templatePropertySettings));
                if ((maxActionButtons === undefined) || (!maxActionButtons) || isNaN(maxActionButtons) || (maxActionButtons <= 0)) {
                    maxActionButtons = defaultItemsViewMobileMaxItemsInRowList;
                }
            } else {
                maxActionButtons = Number(getDefaultValueOfComponent(ITEMS_VIEW_LIST_COMMAND_MAX_ACTION_BUTTONS_IN_ROW, templatePropertySettings));
                if ((maxActionButtons === undefined) || (!maxActionButtons) || isNaN(maxActionButtons) || (maxActionButtons <= 0)) {
                    maxActionButtons = defaultItemsViewMaxItemsInRowList;
                }
            }
            break;
        case LayoutViewType.TABLE:
            if (isMobileView) {
                maxActionButtons = Number(getDefaultValueOfComponent(ITEMS_VIEW_TABLE_COMMAND_MOBILE_MAX_ACTION_BUTTONS_IN_ROW, templatePropertySettings));
                if ((maxActionButtons === undefined) || (!maxActionButtons) || isNaN(maxActionButtons) || (maxActionButtons <= 0)) {
                    maxActionButtons = defaultItemsViewMobileMaxItemsInRowTable;
                }
            } else {
                maxActionButtons = Number(getDefaultValueOfComponent(ITEMS_VIEW_TABLE_COMMAND_MAX_ACTION_BUTTONS_IN_ROW, templatePropertySettings));
                if ((maxActionButtons === undefined) || (!maxActionButtons) || isNaN(maxActionButtons) || (maxActionButtons <= 0)) {
                    maxActionButtons = defaultItemsViewMaxItemsInRowTable;
                }
            }
            break;
        default:
            break;
    }

    return maxActionButtons;
};

const getActionButtonsInRow = (templatePropertySettings: Map<string, TemplatePropertySettings>, isMobileView: boolean, layoutView: LayoutViewType): string[] => {
    let actionButtons: string[] = [];
    switch (layoutView) {
        case LayoutViewType.GRID:
            if (isMobileView) {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_GRID_COMMAND_MOBILE_ACTION_BUTTONS_IN_ROW_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewMobileItemsInRowGrid;
                }
            } else {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_GRID_COMMAND_ACTION_BUTTONS_IN_ROW_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsInRowGrid;
                }
            }
            break;
        case LayoutViewType.LIST:
            if (isMobileView) {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_LIST_COMMAND_MOBILE_ACTION_BUTTONS_IN_ROW_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsMobileInRowList;
                }
            } else {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_LIST_COMMAND_ACTION_BUTTONS_IN_ROW_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsInRowList;
                }
            }
            break;
        case LayoutViewType.TABLE:
            if (isMobileView) {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_TABLE_COMMAND_MOBILE_ACTION_BUTTONS_IN_ROW_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsMobileInRowTable;
                }
            } else {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_TABLE_COMMAND_ACTION_BUTTONS_IN_ROW_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsInRowTable;
                }
            }
            break;
        default:
            break;
    }

    return actionButtons;
};

const getActionButtonsInMore = (templatePropertySettings: Map<string, TemplatePropertySettings>, isMobileView: boolean, layoutView: LayoutViewType, addActionButtons: string[]): string[] => {
    let actionButtons: string[] = [];
    if (!isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_MORE, templatePropertySettings)) {
        return actionButtons;
    }

    switch (layoutView) {
        case LayoutViewType.GRID:
            if (isMobileView) {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_GRID_COMMAND_MOBILE_ACTION_BUTTONS_IN_MORE_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewMobileItemsInMoreGrid;
                }
            } else {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_GRID_COMMAND_ACTION_BUTTONS_IN_MORE_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsInMoreGrid;
                }
            }
            break;
        case LayoutViewType.LIST:
            if (isMobileView) {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_LIST_COMMAND_MOBILE_ACTION_BUTTONS_IN_MORE_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewMobileItemsInMoreList;
                }
            } else {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_LIST_COMMAND_ACTION_BUTTONS_IN_MORE_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsInMoreList;
                }
            }
            break;
        case LayoutViewType.TABLE:
            if (isMobileView) {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_TABLE_COMMAND_MOBILE_ACTION_BUTTONS_IN_MORE_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewMobileItemsInMoreTable;
                }
            } else {
                actionButtons = convertToStringArray([getDefaultValueOfComponent(ITEMS_VIEW_TABLE_COMMAND_ACTION_BUTTONS_IN_MORE_LIST, templatePropertySettings)]);
                if (actionButtons.length === 0) {
                    actionButtons = defaultItemsViewItemsInMoreTable;
                }
            }
            break;
        default:
            break;
    }

    if (addActionButtons.length > 0) {
        return addActionButtons.concat(actionButtons.filter(button => (!addActionButtons.includes(button))));
    }

    return actionButtons;
};

const getApplicableActions = (templatePropertySettings: Map<string, TemplatePropertySettings>, actions: string[], editMode: boolean,
                              publicOnlyAccess: boolean, previewApplicable: boolean, isDownloadable: boolean): string[] => {
    if (actions.length === 0) {
        return [] as string[];
    }

    return actions.filter(action => {
        switch (action) {
            case ITEMS_VIEW_COMMAND_ACTION_EDIT:
                return editMode && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_EDIT, templatePropertySettings);
            case ITEMS_VIEW_COMMAND_ACTION_DELETE:
                return editMode && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_DELETE, templatePropertySettings);
            case ITEMS_VIEW_COMMAND_ACTION_FAVORITE:
                return (!publicOnlyAccess) && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_FAVORITE, templatePropertySettings);
            case ITEMS_VIEW_COMMAND_ACTION_VIEW:
                return isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_VIEW, templatePropertySettings);
            case ITEMS_VIEW_COMMAND_ACTION_PREVIEW:
                return previewApplicable && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_PREVIEW, templatePropertySettings);
            case ITEMS_VIEW_COMMAND_ACTION_COPY_URL:
                return isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_COPY_URL, templatePropertySettings);
            case ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD:
                return isDownloadable && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD, templatePropertySettings);
            case ITEMS_VIEW_COMMAND_ACTION_MORE:
                return isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_MORE, templatePropertySettings);
            default:
                return false;
        }
    });
};

const getCommandActionButtonEdit = (props: CommandActionsBarProps, isEditModeAuthorized: boolean, actionButtonsInRow: string[]) => {
    if (!(isEditModeAuthorized
        && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_EDIT, props.templatePropertySettings)
        && actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_EDIT))) {
        return;
    }

    return (
        <GridCustomButton id={generateIdentifier()}
                          key='edit'
                          icon='edit'
                          mode='tertiary'
                          type='button'
                          title={t('GENERAL_EDIT_LABEL')}
                          onClick={(e) => {
                              executeAction(e, props.onEdit);
                              document.body.click();
                          }}/>
    );
};

const getCommandActionButtonDelete = (props: CommandActionsBarProps, isEditModeAuthorized: boolean, actionButtonsInRow: string[]) => {
    if (!(isEditModeAuthorized
        && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_DELETE, props.templatePropertySettings)
        && actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_DELETE))) {
        return;
    }

    return (
        <GridCustomButton id={generateIdentifier()}
                          key='delete'
                          icon='delete'
                          mode='tertiary'
                          type='button'
                          title={t('GENERAL_DELETE_LABEL')}
                          onClick={(e) => {
                              executeAction(e, props.onDelete);
                              document.body.click();
                          }}/>
    );
};

const getCommandActionButtonFavorite = (props: CommandActionsBarProps, actionButtonsInRow: string[]) => {
    if (!((!props.publicOnlyAccess)
        && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_FAVORITE, props.templatePropertySettings)
        && actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_FAVORITE))) {
        return;
    }

    return (
        <Button id={generateIdentifier()}
                key='favorites'
                icon={props.isFavorite ? 'wishlist-filled' : 'wishlist'}
                mode='tertiary'
                type='button'
                title={t('GENERAL_FAVORITES_LABEL')}
                onClick={(e) => {
                    executeAction(e, props.onFavorite);
                    document.body.click();
                }}/>
    );
};

const getCommandActionButtonView = (props: CommandActionsBarProps, actionButtonsInRow: string[]) => {
    if (!(isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_VIEW, props.templatePropertySettings)
        && actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_VIEW))) {
        return;
    }

    return (
        <GridCustomButton id={generateIdentifier()}
                          key='view'
                          icon='document'
                          mode='tertiary'
                          type='button'
                          title={t('GENERAL_VIEW_LABEL')}
                          onClick={(e) => {
                              executeAction(e, props.onDetails);
                              document.body.click();
                          }}/>
    );
};

const getCommandActionButtonPreview = (props: CommandActionsBarProps, previewApplicable: boolean, actionButtonsInRow: string[]) => {
    if (!(previewApplicable
        && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_PREVIEW, props.templatePropertySettings)
        && actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_PREVIEW))) {
        return;
    }

    return (
        <GridCustomButton id={generateIdentifier()}
                          key='preview'
                          icon='watch-on'
                          mode='tertiary'
                          type='button'
                          title={t('GENERAL_PREVIEW_LABEL')}
                          onClick={(e) => {
                              executeAction(e, props.onPreview);
                              document.body.click();
                          }}/>
    );
};

const getCommandActionButtonCopyUrl = (props: CommandActionsBarProps, actionButtonsInRow: string[]) => {
    if (!(isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_COPY_URL, props.templatePropertySettings)
        && actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_COPY_URL))) {
        return;
    }

    return (
        <GridCustomButton id={generateIdentifier()}
                          key='copy-button'
                          icon='copy'
                          mode='tertiary'
                          type='button'
                          title={t('GENERAL_COPY_URL_LABEL')}
                          onClick={(e) => {
                              executeAction(e, props.onCopyUrl);
                              document.body.click();
                          }}/>
    );
};

const getCommandActionButtonDownload = (props: CommandActionsBarProps, isDownloadable: boolean, actionButtonsInRow: string[]) => {
    if (!(isDownloadable && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD, props.templatePropertySettings)
        && actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD))) {
        return;
    }

    return (
        <Button id={generateIdentifier()}
                key='download'
                icon='download'
                mode='tertiary'
                type='button'
                title={t('GENERAL_DOWNLOAD_LABEL')}
                onClick={(e) => {
                    executeAction(e, props.onDownload);
                    document.body.click();
                }}/>
    );
};

const getCommandActionMenuItemEdit = (props: CommandActionsBarProps, isEditModeAuthorized: boolean, actionButtonsInMore: string[]) => {
    if (!(isEditModeAuthorized
        && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_EDIT, props.templatePropertySettings)
        && actionButtonsInMore.includes(ITEMS_VIEW_COMMAND_ACTION_EDIT))) {
        return;
    }

    return (
        <MenuItem id={generateIdentifier()}
                  key='edit'
                  icon='edit'
                  label={t('GENERAL_EDIT_LABEL')}
                  title={t('GENERAL_EDIT_LABEL')}
                  onClick={(e) => {
                      executeAction(e, props.onEdit);
                      document.body.click();
                  }}/>
    );
};

const getCommandActionMenuItemDelete = (props: CommandActionsBarProps, isEditModeAuthorized: boolean, actionButtonsInMore: string[]) => {
    if (!(isEditModeAuthorized
        && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_DELETE, props.templatePropertySettings)
        && actionButtonsInMore.includes(ITEMS_VIEW_COMMAND_ACTION_DELETE))) {
        return;
    }

    return (
        <MenuItem id={generateIdentifier()}
                  key='delete'
                  icon='delete'
                  label={t('GENERAL_DELETE_LABEL')}
                  title={t('GENERAL_DELETE_LABEL')}
                  onClick={(e) => {
                      executeAction(e, props.onDelete);
                      document.body.click();
                  }}/>
    );
};

const getCommandActionMenuItemFavorite = (props: CommandActionsBarProps, actionButtonsInMore: string[]) => {
    if (!((!props.publicOnlyAccess)
        && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_FAVORITE, props.templatePropertySettings)
        && actionButtonsInMore.includes(ITEMS_VIEW_COMMAND_ACTION_FAVORITE))) {
        return;
    }

    return (
        <MenuItem id={generateIdentifier()}
                  key='favorites'
                  icon={props.isFavorite ? 'wishlist-filled' : 'wishlist'}
                  label={t('GENERAL_FAVORITES_LABEL')}
                  title={t('GENERAL_FAVORITES_LABEL')}
                  onClick={(e) => {
                      executeAction(e, props.onFavorite);
                      document.body.click();
                  }}/>
    );
};

const getCommandActionMenuItemView = (props: CommandActionsBarProps, actionButtonsInMore: string[]) => {
    if (!(isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_VIEW, props.templatePropertySettings)
        && actionButtonsInMore.includes(ITEMS_VIEW_COMMAND_ACTION_VIEW))) {
        return;
    }

    return (
        <MenuItem id={generateIdentifier()}
                  key='view'
                  icon='document'
                  label={t('GENERAL_VIEW_LABEL')}
                  title={t('GENERAL_VIEW_LABEL')}
                  onClick={(e) => {
                      executeAction(e, props.onDetails);
                      document.body.click();
                  }}/>
    );
};

const getCommandActionMenuItemPreview = (props: CommandActionsBarProps, previewApplicable: boolean, actionButtonsInMore: string[]) => {
    if (!(previewApplicable && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_PREVIEW, props.templatePropertySettings)
        && actionButtonsInMore.includes(ITEMS_VIEW_COMMAND_ACTION_PREVIEW))) {
        return;
    }

    return (
        <MenuItem id={generateIdentifier()}
                  key='preview-menu-item'
                  icon='watch-on'
                  label={t('GENERAL_PREVIEW_LABEL')}
                  title={t('GENERAL_PREVIEW_LABEL')}
                  onClick={(e) => {
                      executeAction(e, props.onPreview);
                      document.body.click();
                  }}/>
    );
};

const getCommandActionMenuItemCopyUrl = (props: CommandActionsBarProps, actionButtonsInMore: string[]) => {
    if (!(isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_COPY_URL, props.templatePropertySettings)
        && actionButtonsInMore.includes(ITEMS_VIEW_COMMAND_ACTION_COPY_URL))) {
        return;
    }

    return (
        <MenuItem id={generateIdentifier()}
                  key='copy-menu-item'
                  icon='copy'
                  label={t('GENERAL_COPY_URL_LABEL')}
                  title={t('GENERAL_COPY_URL_LABEL')}
                  onClick={(e) => {
                      executeAction(e, props.onCopyUrl);
                      document.body.click();
                  }}/>
    );
};

const getCommandActionMenuItemDownload = (props: CommandActionsBarProps, isDownloadable: boolean, actionButtonsInMore: string[]) => {
    if (!(isDownloadable && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD, props.templatePropertySettings)
        && actionButtonsInMore.includes(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD))) {
        return;
    }

    return (
        <MenuItem id={generateIdentifier()}
                  key='download'
                  icon='download'
                  label={t('GENERAL_DOWNLOAD_LABEL')}
                  title={t('GENERAL_DOWNLOAD_LABEL')}
                  onClick={(e) => {
                      executeAction(e, props.onDownload);
                      document.body.click();
                  }}/>
    );
};

const getCommandActionMoreMenu = (props: CommandActionsBarProps, enableMoreButton: boolean, isEditModeAuthorized: boolean, previewApplicable: boolean, actionButtonsInMore: string[], isDownloadable: boolean) => {
    if (!enableMoreButton) {
        return;
    }

    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => {
        setIsOpen(!isOpen);
        document.body.click();
    };

    return (
        <Popover id={generateIdentifier('popover-'.concat(props.item.id), '-')}
                 open={isOpen}
                 position='top-right'
                 onCloseButtonClick={toggle}
                 onCloseKeyPressed={toggle}
                 onOutsideClick={toggle}
                 onTriggerClick={toggle}
                 paragraph={
                     <div id={generateIdentifier()}>
                         {actionButtonsInMore?.map(action => {
                             switch (action) {
                                 case ITEMS_VIEW_COMMAND_ACTION_EDIT:
                                     return getCommandActionMenuItemEdit(props, isEditModeAuthorized, actionButtonsInMore);
                                 case ITEMS_VIEW_COMMAND_ACTION_DELETE:
                                     return getCommandActionMenuItemDelete(props, isEditModeAuthorized, actionButtonsInMore);
                                 case ITEMS_VIEW_COMMAND_ACTION_FAVORITE:
                                     return getCommandActionMenuItemFavorite(props, actionButtonsInMore);
                                 case ITEMS_VIEW_COMMAND_ACTION_VIEW:
                                     return getCommandActionMenuItemView(props, actionButtonsInMore);
                                 case ITEMS_VIEW_COMMAND_ACTION_PREVIEW:
                                     return getCommandActionMenuItemPreview(props, previewApplicable, actionButtonsInMore);
                                 case ITEMS_VIEW_COMMAND_ACTION_COPY_URL:
                                     return getCommandActionMenuItemCopyUrl(props, actionButtonsInMore);
                                 case ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD:
                                     return getCommandActionMenuItemDownload(props, isDownloadable, actionButtonsInMore);
                                 default:
                                     break;
                             }
                         })}
                     </div>
                 }
                 trigger={
                     <Button id={generateIdentifier()}
                             key='more'
                             icon='options'
                             mode='tertiary'
                             type='button'
                             title={t('GENERAL_OPTIONS_LABEL')}
                             onClick={(e) => executeAction(e, toggle)}/>
                 }
        />
    );
};

/**
 * @name CommandActionsBar
 * @description
 * Component to add command actions to a Storage Object
 */
export const CommandActionsBar: React.VFC<CommandActionsBarProps> = (props) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [isAuthorizedWriteOwnerAccess] = useState<boolean>(hasWriteOwnerAccess(isAuthenticated, roles, props.item.metadata.ownerRoles, props.item.metadata.parentOwnerRoles));
    const isEditModeAuthorized = (props.editMode && isAuthorizedWriteOwnerAccess) as boolean;
    if (!isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION, props.templatePropertySettings)) {
        return null;
    }

    const isFileItem = isFile(props.item.objectType);
    let isDownloadActive = isDownloadFeatureActive(props.layoutView);
    let isDownloadable = (isFileItem && isDownloadActive && isFileDownloadable(props.item.status)) as boolean;
    let previewApplicable = (isFileItem && isPreviewApplicable(props.item.metadata.contentType, getFileExtension(props.item.metadata.blobUrl))) as boolean;
    let maxActionButtonsInRow = getDefaultMaxActionButtonsInRow(props.templatePropertySettings, props.isMobileView, props.layoutView);
    let actionButtonsInRowConfigured = getApplicableActions(props.templatePropertySettings, getActionButtonsInRow(props.templatePropertySettings, props.isMobileView, props.layoutView), props.editMode, props.publicOnlyAccess, previewApplicable, isDownloadable);
    let actionButtonsCount = actionButtonsInRowConfigured.length;
    let slicePosition = isFileItem ? (maxActionButtonsInRow - 1) : maxActionButtonsInRow;
    let isToSlice = (actionButtonsCount > slicePosition) as boolean;
    let actionButtonsInRow = isToSlice ? actionButtonsInRowConfigured.slice(0, slicePosition) : actionButtonsInRowConfigured;
    let actionButtonsInRowOverflow = isToSlice ? actionButtonsInRow.slice(slicePosition, actionButtonsCount) : [];
    let isActiveMoreButton = isToSlice && isVisibleComponent(ITEMS_VIEW_COMMAND_ACTION_MORE, props.templatePropertySettings);
    let actionButtonsInMore = isActiveMoreButton ?
        getApplicableActions(props.templatePropertySettings,
            getActionButtonsInMore(props.templatePropertySettings, props.isMobileView, props.layoutView, actionButtonsInRowOverflow), props.editMode, props.publicOnlyAccess, previewApplicable, isDownloadable)
            .filter(button => (!actionButtonsInRow.includes(button))) : [];
    let enableMoreButton = (isActiveMoreButton && (actionButtonsInMore.length > 0)) as boolean;
    if (enableMoreButton && (!actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_MORE))) {
        actionButtonsInRow = actionButtonsInRow.concat(ITEMS_VIEW_COMMAND_ACTION_MORE);
    } else {
        actionButtonsInRow = actionButtonsInRow.filter(action => (action !== ITEMS_VIEW_COMMAND_ACTION_MORE));
    }

    if (isDownloadActive && (actionButtonsInRowConfigured.includes(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD))
        && (!actionButtonsInRow.includes(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD))) {
        actionButtonsInRow = actionButtonsInRow.concat(ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD);
    }

    return (
        <CommandActionWrapper id={generateIdentifier()}
                              isReversed={reverseDirection(props.layoutView)}>
            {actionButtonsInRow?.map(action => {
                    switch (action) {
                        case ITEMS_VIEW_COMMAND_ACTION_EDIT:
                            return getCommandActionButtonEdit(props, isEditModeAuthorized, actionButtonsInRow);
                        case ITEMS_VIEW_COMMAND_ACTION_DELETE:
                            return getCommandActionButtonDelete(props, isEditModeAuthorized, actionButtonsInRow);
                        case ITEMS_VIEW_COMMAND_ACTION_FAVORITE:
                            return getCommandActionButtonFavorite(props, actionButtonsInRow);
                        case ITEMS_VIEW_COMMAND_ACTION_VIEW:
                            return getCommandActionButtonView(props, actionButtonsInRow);
                        case ITEMS_VIEW_COMMAND_ACTION_PREVIEW:
                            return getCommandActionButtonPreview(props, previewApplicable, actionButtonsInRow);
                        case ITEMS_VIEW_COMMAND_ACTION_COPY_URL:
                            return getCommandActionButtonCopyUrl(props, actionButtonsInRow);
                        case ITEMS_VIEW_COMMAND_ACTION_DOWNLOAD:
                            return getCommandActionButtonDownload(props, isDownloadable, actionButtonsInRow);
                        case ITEMS_VIEW_COMMAND_ACTION_MORE:
                            return getCommandActionMoreMenu(props, enableMoreButton, isEditModeAuthorized, previewApplicable, actionButtonsInMore, isDownloadable);
                        default:
                            break;
                    }
                }
            )}
        </CommandActionWrapper>
    );
};